import React, { useEffect, useMemo, useState } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  IconButton,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { PAYMENT_METHOD_TYPE } from '@src/constants/payment';
import {
  NAPAS_BANK_SEARCH_FIELDS,
  getNapasBankOptions,
} from '@src/constants/napasBanks';
import { COLOR } from '@src/styles/color';
import CommonInput from '@src/new-components/Input/CommonInput';
import SelectSearch from '@src/new-components/Select/SelectSearch';

const EMPTY_FORM = {
  number: '',
  issueDate: '',
  nameOnCard: '',
  issuerCode: '',
  issuerName: '',
  accountNumber: '',
  accountName: '',
  identificationNumber: '',
};

const FIELD_LABELS = {
  number: 'napasCardNumber',
  issueDate: 'napasIssueDate',
  nameOnCard: 'napasNameOnCard',
  issuerCode: 'napasIssuerCode',
  accountNumber: 'napasAccountNumber',
  accountName: 'napasAccountName',
  identificationNumber: 'napasIdentificationNumber',
};

const FIELD_PLACEHOLDERS = {
  number: 'napasCardNumberPlaceholder',
  issueDate: 'napasIssueDatePlaceholder',
  nameOnCard: 'napasNameOnCardPlaceholder',
  issuerCode: 'napasIssuerCodePlaceholder',
  accountNumber: 'napasAccountNumberPlaceholder',
  accountName: 'napasAccountNamePlaceholder',
  identificationNumber: 'napasIdentificationNumberPlaceholder',
};

const FIELD_INVALID_MESSAGES = {
  number: 'napasCardNumberInvalid',
  issueDate: 'napasIssueDateInvalid',
  accountNumber: 'napasAccountNumberInvalid',
  identificationNumber: 'napasIdentificationNumberInvalid',
};

const getRequiredFields = (paymentMethodType) => {
  if (paymentMethodType === PAYMENT_METHOD_TYPE.NAPAS_CARD)
    return ['number', 'issueDate', 'nameOnCard'];

  if (paymentMethodType === PAYMENT_METHOD_TYPE.NAPAS_ACCOUNT)
    return [
      'issuerCode',
      'accountNumber',
      'accountName',
      'identificationNumber',
    ];

  return [];
};

const DIGIT_ONLY_FIELDS = ['number', 'accountNumber'];

const FIELD_MAX_LENGTHS = {
  number: 19,
  nameOnCard: 100,
  accountNumber: 100,
  accountName: 100,
  identificationNumber: 100,
};

const normalizeIssueDate = (value) => value.replace(/\D/g, '');

const formatIssueDate = (value) => {
  const normalizedValue = normalizeIssueDate(value).slice(0, 4);
  if (normalizedValue.length <= 2) return normalizedValue;

  return `${normalizedValue.slice(0, 2)}/${normalizedValue.slice(2)}`;
};

const isFutureIssueDate = (value) => {
  const normalizedValue = normalizeIssueDate(value);
  const month = Number(normalizedValue.slice(0, 2));
  const year = Number(`20${normalizedValue.slice(2, 4)}`);
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  return year > currentYear || (year === currentYear && month > currentMonth);
};

const validateField = ({ field, value, t }) => {
  const trimmedValue = value.trim();
  if (!trimmedValue)
    return t('requiredField', { field: t(FIELD_LABELS[field]) });

  if (field === 'number' && !/^\d{1,19}$/.test(trimmedValue.replace(/\s/g, '')))
    return t(FIELD_INVALID_MESSAGES[field]);

  if (
    field === 'issueDate' &&
    (!/^(0[1-9]|1[0-2])\d{2}$/.test(normalizeIssueDate(trimmedValue)) ||
      isFutureIssueDate(trimmedValue))
  )
    return t(FIELD_INVALID_MESSAGES[field]);

  if (
    field === 'accountNumber' &&
    !/^\d{1,100}$/.test(trimmedValue.replace(/\s/g, ''))
  )
    return t(FIELD_INVALID_MESSAGES[field]);

  if (
    field === 'identificationNumber' &&
    !/^[a-zA-Z0-9]{1,100}$/.test(trimmedValue.replace(/\s/g, ''))
  )
    return t(FIELD_INVALID_MESSAGES[field]);

  return '';
};

const buildPaymentSource = (paymentMethodType, form) => {
  if (paymentMethodType === PAYMENT_METHOD_TYPE.NAPAS_CARD) {
    return {
      number: form.number.replace(/\s/g, ''),
      issueDate: normalizeIssueDate(form.issueDate),
      nameOnCard: form.nameOnCard.trim().toUpperCase(),
    };
  }

  return {
    issuerCode: form.issuerCode,
    accountNumber: form.accountNumber.trim(),
    accountName: form.accountName.trim().toUpperCase(),
    identificationNumber: form.identificationNumber.replace(/\s/g, ''),
  };
};

const NapasPaymentSourceDialog = ({
  open,
  loading = false,
  paymentMethodType,
  onClose,
  onConfirm,
}) => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState({});

  const isCard = paymentMethodType === PAYMENT_METHOD_TYPE.NAPAS_CARD;
  const requiredFields = useMemo(
    () => getRequiredFields(paymentMethodType),
    [paymentMethodType],
  );
  const bankOptions = useMemo(
    () =>
      getNapasBankOptions(i18n.language).map((bank) => ({
        ...bank,
        name: bank.label,
        value: bank.value,
      })),
    [i18n.language],
  );

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setTouched({});
    }
  }, [open]);

  const errors = useMemo(
    () =>
      requiredFields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: validateField({ field, value: form[field] || '', t }),
        }),
        {},
      ),
    [form, requiredFields, t],
  );

  const isValid = requiredFields.every((field) => !errors[field]);

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    const shouldUppercase = ['nameOnCard', 'accountName'].includes(field);
    let nextValue = value;
    if (field === 'issueDate') nextValue = formatIssueDate(value);
    else if (DIGIT_ONLY_FIELDS.includes(field))
      nextValue = value.replace(/\D/g, '');

    const maxLength = FIELD_MAX_LENGTHS[field];
    if (maxLength && nextValue.length > maxLength)
      nextValue = nextValue.slice(0, maxLength);

    setForm((prev) => ({
      ...prev,
      [field]: shouldUppercase ? nextValue.toUpperCase() : nextValue,
    }));
  };

  const handleBlur = (field) => () =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const handleChangeBank = (option) => {
    setForm((prev) => ({
      ...prev,
      issuerCode: option?.value || '',
      issuerName: option?.name || '',
    }));
    setTouched((prev) => ({ ...prev, issuerCode: true }));
  };

  const handleConfirm = () => {
    setTouched(
      requiredFields.reduce((acc, field) => ({ ...acc, [field]: true }), {}),
    );

    if (!isValid) return;

    onConfirm(buildPaymentSource(paymentMethodType, form));
  };

  const renderTextField = (field) => (
    <CommonInput
      key={field}
      label={t(FIELD_LABELS[field])}
      placeholder={t(FIELD_PLACEHOLDERS[field])}
      value={form[field]}
      onChange={handleChange(field)}
      onBlur={handleBlur(field)}
      error={Boolean(touched[field] && errors[field])}
      helperText={touched[field] ? errors[field] : ''}
      inputProps={{
        autoComplete: 'off',
        ...(field === 'issueDate'
          ? { inputMode: 'numeric', maxLength: 5 }
          : {}),
        ...(DIGIT_ONLY_FIELDS.includes(field) ? { inputMode: 'numeric' } : {}),
        ...(FIELD_MAX_LENGTHS[field]
          ? { maxLength: FIELD_MAX_LENGTHS[field] }
          : {}),
      }}
    />
  );

  return (
    <StyledNapasDialog open={open} onClose={loading ? undefined : onClose}>
      <Box className="napas-dialog-header">
        <Typography className="napas-dialog-title">
          {isCard ? t('napasCardInfo') : t('napasAccountInfo')}
        </Typography>
        <IconButton className="napas-dialog-close" onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <Box className="napas-dialog-content">
        {isCard ? (
          <>
            {renderTextField('number')}
            {renderTextField('issueDate')}
            {renderTextField('nameOnCard')}
          </>
        ) : (
          <>
            <Box className="napas-bank-select">
              <SelectSearch
                label={t(FIELD_LABELS.issuerCode)}
                placeholder={t(FIELD_PLACEHOLDERS.issuerCode)}
                value={form.issuerName}
                onChange={handleChangeBank}
                error={Boolean(touched.issuerCode && errors.issuerCode)}
                helperText={touched.issuerCode ? errors.issuerCode : ''}
                options={bankOptions}
                searchFields={NAPAS_BANK_SEARCH_FIELDS}
              />
            </Box>
            {renderTextField('accountNumber')}
            {renderTextField('accountName')}
            {renderTextField('identificationNumber')}
          </>
        )}
      </Box>
      <DialogActions className="napas-dialog-actions">
        <Button
          className="napas-dialog-cancel"
          variant="outlined"
          onClick={onClose}
          disabled={loading}
        >
          {t('cancel')}
        </Button>
        <Button
          className="napas-dialog-confirm"
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || !isValid}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t('confirm')
          )}
        </Button>
      </DialogActions>
    </StyledNapasDialog>
  );
};

const StyledNapasDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    width: 512px;
    max-width: calc(100vw - 32px);
    overflow: hidden;
  }

  .napas-dialog-header {
    align-items: center;
    border-bottom: 1px solid rgba(60, 60, 67, 0.16);
    display: flex;
    height: 56px;
    justify-content: space-between;
    padding: 0 24px;
  }

  .napas-dialog-title {
    color: ${COLOR.darkGray};
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
  }

  .napas-dialog-close {
    color: rgba(30, 22, 78, 0.64);
    margin-right: -8px;
  }

  .napas-dialog-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;

    .input-label {
      color: ${COLOR.darkGray};
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      margin-bottom: 4px;
    }

    .MuiInputBase-root {
      border-radius: 8px;
      min-height: 40px;
    }

    .MuiOutlinedInput-input {
      font-size: 14px;
      line-height: 20px;
      padding: 10px 12px;
    }

    .MuiFormHelperText-root {
      margin-left: 0;
    }

    .napas-bank-select {
      .select-btn-select-search {
        margin-top: 4px;
      }
    }
  }

  .napas-dialog-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    padding: 0 24px 24px;

    .MuiButton-root {
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      height: 40px;
      min-width: 76px;
      padding: 8px 24px;
      text-transform: none;
    }
  }

  .napas-dialog-cancel {
    border-color: rgba(30, 22, 78, 0.16);
    color: ${COLOR.darkBlue};
  }

  .napas-dialog-confirm {
    background-color: ${COLOR.primary};
    color: ${COLOR.darkBlue};

    &:hover {
      background-color: ${COLOR.primaryHover};
    }

    &.Mui-disabled {
      background-color: ${COLOR.primary};
      color: ${COLOR.darkBlue};
      cursor: not-allowed;
      opacity: 0.5;
      pointer-events: auto;

      &:hover {
        background-color: ${COLOR.primary};
      }
    }
  }
`;

export default NapasPaymentSourceDialog;
